import React from 'react';
import { useTranslate } from 'react-admin';
import { Alert, Box, Card, Divider, Skeleton, Stack, Typography } from '@mui/material';
import { BlockHeading, StatusDot } from '@/components/shell';
import { HealthDot } from './HealthDot';
import type { GatewayHealthStatus } from '../types';

const Row: React.FC<{ title: React.ReactNode; detail?: string; status: React.ReactNode }> = ({ title, detail, status }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1 }}>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2">{title}</Typography>
      {detail && <Typography variant="body2" color="text.secondary">{detail}</Typography>}
    </Box>
    <Box sx={{ flexShrink: 0 }}>{status}</Box>
  </Box>
);

const Capability: React.FC<{ name: string; detail: string; available: boolean | undefined }> = ({ name, detail, available }) => {
  const t = useTranslate();
  const status = available === undefined
    ? <StatusDot tone="neutral" label={t('screens.techSupport.unknown')} />
    : <StatusDot tone={available ? 'ok' : 'problem'} label={t(available ? 'screens.techSupport.available' : 'screens.techSupport.unavailable')} />;
  return <Row title={name} detail={detail} status={status} />;
};

/**
 * What the gateway reports about its OCR servers and the checks it runs itself. Shared by the
 * system overview and the KYC services screen, which both show it.
 */
export const GatewayHealthCard: React.FC<{ health: GatewayHealthStatus | null; loading: boolean }> = ({ health, loading }) => {
  const t = useTranslate();
  const ocr = health?.ocr;
  const capabilities = health?.localCapabilities;

  return (
    <Box component="section" aria-labelledby="gateway-health" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <BlockHeading id="gateway-health" title={t('tech_support.gateway_health.title')} />
      <Card sx={{ p: 2, flex: 1 }}>
        {loading ? (
          <Stack spacing={1}>{[...Array(4)].map((_, i) => <Skeleton key={i} height={36} />)}</Stack>
        ) : !health ? (
          <Typography variant="body2" color="text.secondary">{t('tech_support.gateway_health.unavailable')}</Typography>
        ) : (
          <Stack spacing={2}>
            <Alert severity={ocr?.available ? 'success' : 'error'}>
              {t(ocr?.available ? 'tech_support.gateway_health.ocr_available' : 'tech_support.gateway_health.ocr_unavailable')}
            </Alert>

            <Box>
              <Typography variant="subtitle2">{t('tech_support.gateway_health.ocr_servers')}</Typography>
              {ocr?.primary || ocr?.secondary ? (
                <Stack divider={<Divider />}>
                  {ocr?.primary && (
                    <Row
                      title={ocr.primary.name}
                      detail={t('screens.techSupport.primary')}
                      status={<HealthDot status={ocr.primary.healthy ? 'healthy' : 'down'} />}
                    />
                  )}
                  {ocr?.secondary && (
                    <Row
                      title={ocr.secondary.name}
                      detail={t('screens.techSupport.secondary')}
                      status={<HealthDot status={ocr.secondary.healthy ? 'healthy' : 'down'} />}
                    />
                  )}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  {t('tech_support.gateway_health.no_ocr_servers')}
                </Typography>
              )}
            </Box>

            <Box>
              <Typography variant="subtitle2">{t('tech_support.gateway_health.local_capabilities')}</Typography>
              <Stack divider={<Divider />}>
                <Capability
                  name={t('screens.techSupport.faceMatch')}
                  detail={t('tech_support.gateway_health.facematch_tooltip')}
                  available={capabilities?.faceMatch}
                />
                <Capability
                  name={t('screens.techSupport.miDniQr')}
                  detail={t('tech_support.gateway_health.midni_tooltip')}
                  available={capabilities?.miDniQr}
                />
              </Stack>
            </Box>
          </Stack>
        )}
      </Card>
    </Box>
  );
};
